import { faCakeCandles } from "@fortawesome/free-solid-svg-icons/faCakeCandles";
import { faThumbsDown } from "@fortawesome/free-solid-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/free-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect } from "react";
import { AppContext } from "~/contexts/AppContext";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons/faCircleQuestion";
import { faMedal } from "@fortawesome/free-solid-svg-icons/faMedal";
import { useTranslation } from "react-i18next";
import { fixBold } from "~/util/util";
import { GroupResult } from "~/types";
import TypingTextV2 from "~/components/TypingTextV2";
import styled from "styled-components";

const AGE_GROUPS = [
    { emoji: '👶', label: '-18', middleAge: 15 },
    { emoji: '🧑‍🎓', label: '18-29', middleAge: 24 },
    { emoji: '👨', label: '30-44', middleAge: 37 },
    { emoji: '🧑‍💼', label: '45-64', middleAge: 55 },
    { emoji: '👴', label: '65+', middleAge: 70 },
] as const;

const AgeGroupButton = styled.button`
    background-color: #F0F1F5;
    border: none;
    border-radius: 10px;
    padding: 8px 0;
    color: #202020;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: center;
    white-space: nowrap;
    
    @media only screen and (max-width: 450px) {
        padding: 7px 0;
        font-size: 12px;
        gap: 4px;
    }
    
    @media only screen and (max-width: 350px) {
        padding: 6px 0;
        font-size: 11px;
        gap: 3px;
    }
`;

const AgeGroupButtonContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 15px;
    width: 100%;
    
    @media only screen and (max-width: 450px) {
        gap: 8px;
    }
    
    @media only screen and (max-width: 350px) {
        gap: 6px;
    }
`;

const PrivacyText = styled.p`
    font-size: 12px !important;
    color: #ccc !important;
    margin: 8px 0 0 0 !important;
    
    @media only screen and (max-width: 450px) {
        font-size: 11px !important;
    }
    
    @media only screen and (max-width: 350px) {
        font-size: 10px !important;
    }
`;

interface AgeResultSlideProps {
    groupResult: GroupResult;
    ratingValue: number;
    submitBirthYear: React.ChangeEventHandler;
    fadeInComplete: boolean;
    onAnimationComplete: () => void;
}

const AgeResultSlide: React.FC<AgeResultSlideProps> = (props) => {
    const { profile } = useContext(AppContext)!;
    const { t } = useTranslation();
    
    if(!profile) return null;

    const handleAgeGroupSelect = (middleAge: number) => {
        const birthYear = new Date().getFullYear() - middleAge;
        console.log(birthYear);
        const event = {
            target: { value: birthYear.toString() }
        } as React.ChangeEvent<HTMLSelectElement>;
        props.submitBirthYear(event);
    };

    useEffect(() => {
        if (!profile.birthYear && props.fadeInComplete) {
            props.onAnimationComplete();
        }
    }, [props.fadeInComplete, profile.birthYear]);

    if (!profile.birthYear || !props.groupResult)
        return (
            <>
                <span className='header-icon-row'>
                    <span className='icon'><FontAwesomeIcon icon={faCakeCandles} /></span>
                    <span className='icon'><FontAwesomeIcon icon={faCircleQuestion} /></span>
                </span>
                <p style={{ fontSize: '16px', marginBottom: '10px', marginTop: '0', fontWeight: 'bold' }}>{t('results_age_input_let_me_show_you')}</p>
                <AgeGroupButtonContainer>
                    {AGE_GROUPS.map(group => (
                        <AgeGroupButton 
                            key={group.label}
                            onClick={() => handleAgeGroupSelect(group.middleAge)}
                        >
                            <span>{group.emoji}</span>
                            {group.label}
                        </AgeGroupButton>
                    ))}
                </AgeGroupButtonContainer>
                <PrivacyText>{t('results_age_input_disclaimer')}</PrivacyText>
            </>
        );

    if (props.groupResult.firstRating) {
        return (
            <>
                <span className='header-icon-row'>
                    <span className='icon'><FontAwesomeIcon icon={faCakeCandles} /></span>
                    <span className='icon'><FontAwesomeIcon icon={faMedal} /></span>
                </span>
                <TypingTextV2 active={props.fadeInComplete} delay={10} onComplete={props.onAnimationComplete}>
                    <p>
                        {fixBold(t('results_age_first_rating'))}
                    </p>
                </TypingTextV2>
            </>
        );
    }

    if (props.groupResult.rating === null)
        return null;

    const averageRating: number = props.groupResult.rating;
    const userRating = props.ratingValue;

    const ratingsDiff = Math.abs(userRating - averageRating);

    let agreementMessage;
    let agreementIcon;

    if(ratingsDiff <= 15) {
        agreementIcon = faThumbsUp;
        agreementMessage = fixBold(t('results_age_strong_agreement'));
    } else if (ratingsDiff <= 30) {
        agreementIcon = faThumbsUp;
        agreementMessage = fixBold(t('results_age_weak_agreement'));
    } else if(ratingsDiff <= 50) {
        agreementIcon = faThumbsDown;
        agreementMessage = fixBold(t('results_age_weak_disagreement'));
    } else {
        agreementIcon = faThumbsDown;
        agreementMessage = fixBold(t('results_age_strong_disagreement'));
    }

    return (
        <>
            <span className='header-icon-row'>
                <span className='icon'><FontAwesomeIcon icon={faCakeCandles} /></span>
                <span className='icon'><FontAwesomeIcon icon={agreementIcon} /></span>
            </span>
            <TypingTextV2 active={props.fadeInComplete} delay={10} onComplete={props.onAnimationComplete}>
                <p>{agreementMessage}</p>
            </TypingTextV2>
        </>
    );
};

export default AgeResultSlide;